import React from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { register, login } from "../../../service/auth/authService";
import { User } from "../../../types/userTypes";
import logo from "../../../assets/logo/uxa_long_logo_white.png";
import background from "../../../assets/background/background_1.jpg";

const Register: React.FC = () => {
    const navigate = useNavigate();

    // ✅ Validation Schema
    const validationSchema = Yup.object({
        name: Yup.string().required("Name is required").max(255, "Max length is 255"),
        username: Yup.string().required("Username is required").max(255, "Max length is 255"),
        email: Yup.string().required("Email is required").email("Invalid email").max(255, "Max length is 255"),
        password: Yup.string().required("Password is required").min(8, "Must be at least 8 characters"),
    });

    // ✅ Initial Form Values
    const initialValues = {
        name: "",
        username: "",
        email: "",
        password: "",
    };

    // ✅ Form Submit Handler
    const handleSubmit = async (values: typeof initialValues, { setSubmitting }: any) => {
        try {
            // Register the user
            const registerResponse = await register(values.name, values.username, values.email, values.password);

            if (!registerResponse) return;

            const loginResponse = await login(values.username, values.password);

            // Extract token and user data
            const { token, user }: { token: string; user: User } = loginResponse.data;

            // Store token & user info
            localStorage.setItem("token", token);
            localStorage.setItem("user", JSON.stringify(user));

            // Redirect to the dashboard or homepage
            navigate("/folderRepo");
        } catch (error: any) {
            console.error("Error:", error.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="flex flex-col md:flex-row min-h-screen">
            {/* Left Side Banner with Background Image and Grey Overlay */}
            <div
                className="w-full md:w-3/5 flex flex-col items-center justify-center text-white p-10 md:p-16 relative bg-cover bg-center"
                style={{ backgroundImage: `url(${background})` }}
            >
                {/* Opacity Grey Overlay */}
                <div className="absolute inset-0 bg-black opacity-50"></div>

                <div className="relative z-10 text-center">
                    <h1 className="text-3xl md:text-5xl font-bold flex items-center space-x-2">
                        <span>Virtual Data Room</span>
                    </h1>
                </div>
                <div className="relative z-10 text-left mt-1">
                    <span className="text-sm md:text-lg font-semibold mt-1 flex items-center space-x-2">
                        <span>by</span>
                        <img src={logo} alt="Logo" className="w-48 md:w-52 h-auto" />
                    </span>
                </div>

                {/* Copyright Notice */}
                <p className="absolute bottom-4 text-sm text-gray-300 text-center">
                    &copy; {new Date().getFullYear()} Virtual Data Room | Uxatelier. All rights reserved.
                </p>
            </div>

            {/* Right Side Register Form */}
            <div className="w-full md:w-2/5 flex flex-col justify-center p-10 md:p-20">
                <h2 className="text-3xl md:text-5xl font-bold text-gray-800 mb-6 md:mb-8">Register</h2>

                {/* Register Form */}
                <div className="w-full max-w-md">
                    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
                        {({ isSubmitting }) => (
                            <Form className="space-y-4">
                                {/* Name */}
                                <div>
                                    <label className="block text-lg font-medium text-gray-700">Name</label>
                                    <Field type="text" name="name" className="w-full mt-2 p-3 border rounded-lg text-lg focus:ring-2 focus:ring-[#632DE9]" />
                                    <ErrorMessage name="name" component="div" className="text-red-500 text-sm" />
                                </div>

                                {/* Username */}
                                <div>
                                    <label className="block text-lg font-medium text-gray-700">Username</label>
                                    <Field type="text" name="username" className="w-full mt-2 p-3 border rounded-lg text-lg focus:ring-2 focus:ring-[#632DE9]" />
                                    <ErrorMessage name="username" component="div" className="text-red-500 text-sm" />
                                </div>

                                {/* Email */}
                                <div>
                                    <label className="block text-lg font-medium text-gray-700">Email</label>
                                    <Field type="email" name="email" className="w-full mt-2 p-3 border rounded-lg text-lg focus:ring-2 focus:ring-[#632DE9]" />
                                    <ErrorMessage name="email" component="div" className="text-red-500 text-sm" />
                                </div>

                                {/* Password */}
                                <div>
                                    <label className="block text-lg font-medium text-gray-700">Password</label>
                                    <Field type="password" name="password" className="w-full mt-2 p-3 border rounded-lg text-lg focus:ring-2 focus:ring-[#632DE9]" />
                                    <ErrorMessage name="password" component="div" className="text-red-500 text-sm" />
                                </div>

                                {/* Submit Button */}
                                <button
                                    type="submit"
                                    className="w-full mt-6 bg-[#632DE9] hover:bg-[#4A1FB8] text-white font-bold py-2 px-3 rounded-lg text-lg transition duration-300"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? "Registering..." : "Register"}
                                </button>

                                {/* Login Link */}
                                <p className="text-center text-sm text-gray-600 mt-3">
                                    Already have an account?{" "}
                                    <a href="/login" className="text-blue-500 hover:underline">
                                        Login here
                                    </a>
                                </p>
                            </Form>
                        )}
                    </Formik>
                </div>
            </div>
        </div>
    );
};

export default Register;
